const APPROVED_REMOTE_MEDIA_HOSTS = ['images.ctfassets.net', 'downloads.ctfassets.net', 'd8xhyovu00185.cloudfront.net'];
const NETLIFY_IMAGE_CDN_UNSUPPORTED_EXTENSIONS = new Set(['gif']);

// Keep this empty until a specific media transform is intentionally supported.
export const PUBLIC_MEDIA_QUERY_PARAM_ALLOWLIST = [];
export const NETLIFY_IMAGE_WIDTHS = [480, 768, 1200, 1600];

function normalizeProtocolRelativeUrl(value) {
  return value.startsWith('//') ? `https:${value}` : value;
}

function buildAllowedSearch(entries) {
  if (!entries.length) {
    return '';
  }

  const sortedEntries = [...entries].sort(([leftKey, leftValue], [rightKey, rightValue]) => {
    if (leftKey !== rightKey) {
      return leftKey.localeCompare(rightKey);
    }

    return leftValue.localeCompare(rightValue);
  });

  return new URLSearchParams(sortedEntries).toString();
}

function getPublicMediaPathname(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  if (value.startsWith('/')) {
    return value.split(/[?#]/)[0];
  }

  try {
    return new URL(value).pathname;
  } catch (error) {
    return value.split(/[?#]/)[0];
  }
}

function getPublicMediaExtension(value) {
  const pathname = getPublicMediaPathname(value).toLowerCase();
  const extensionMatch = pathname.match(/\.([a-z0-9]+)$/);

  return extensionMatch ? extensionMatch[1] : '';
}

export function isApprovedRemoteMediaHost(hostname = '') {
  return APPROVED_REMOTE_MEDIA_HOSTS.includes(String(hostname).toLowerCase());
}

export function canonicalizePublicMediaUrl(value, { preserveParams = PUBLIC_MEDIA_QUERY_PARAM_ALLOWLIST } = {}) {
  if (typeof value !== 'string' || !value.trim()) {
    return value;
  }

  const normalizedValue = normalizeProtocolRelativeUrl(value.trim());

  if (normalizedValue.startsWith('/')) {
    return normalizedValue.split(/[?#]/)[0];
  }

  if (!/^https?:\/\//i.test(normalizedValue)) {
    return normalizedValue;
  }

  try {
    const parsedUrl = new URL(normalizedValue);

    if (!isApprovedRemoteMediaHost(parsedUrl.hostname)) {
      return normalizedValue;
    }

    const preserveParamSet = new Set(preserveParams);
    const preservedEntries = [...parsedUrl.searchParams.entries()].filter(([key]) => preserveParamSet.has(key));
    const allowedSearch = buildAllowedSearch(preservedEntries);

    parsedUrl.search = allowedSearch ? `?${allowedSearch}` : '';
    parsedUrl.hash = '';

    return parsedUrl.toString();
  } catch (error) {
    return normalizedValue;
  }
}

export function isNetlifyOptimizableMediaUrl(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  const normalizedValue = canonicalizePublicMediaUrl(value);
  const extension = getPublicMediaExtension(normalizedValue);

  // Animated GIFs should bypass transforms so they keep animating and avoid
  // known transform failures from the Netlify image endpoint.
  if (NETLIFY_IMAGE_CDN_UNSUPPORTED_EXTENSIONS.has(extension)) {
    return false;
  }

  if (normalizedValue.startsWith('/')) {
    return true;
  }

  try {
    return isApprovedRemoteMediaHost(new URL(normalizedValue).hostname);
  } catch (error) {
    return false;
  }
}
